import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, DatePicker, message, Tag, Spin } from 'antd';
import { getBundles, assignBundleToPermission, updateBundleAssignment } from '../../services/api';
import { timeConverter } from '../utils/TimeConverter';
import dayjs from 'dayjs';

const { Option } = Select;

const BundleModal = ({ visible, onCancel, onSuccess, permissionId, userId, bundle, mode, assignedBundles = [] }) => {
    const [form] = Form.useForm();
    const [existingBundles, setExistingBundles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [bundlesLoading, setBundlesLoading] = useState(false);
    const [remainingDays, setRemainingDays] = useState(0);

    const fetchExistingBundles = useCallback(async () => {
        try {
            setBundlesLoading(true);
            const bundles = await getBundles();
            setExistingBundles(bundles);
        } catch (error) {
            console.error('Error fetching bundles:', error);
            message.error('Failed to fetch existing bundles');
        } finally {
            setBundlesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            fetchExistingBundles();
            if (mode === 'edit' && bundle) {
                const startTime = dayjs(bundle.start_time);
                const expireTime = dayjs(bundle.expire_time);
                form.setFieldsValue({
                    bundle_id: bundle.id,
                    start_time: startTime,
                    expire_time: expireTime
                });
                calculateRemainingDays(startTime, expireTime);
            } else {
                form.resetFields();
                setRemainingDays(0);
            }
        }
    }, [visible, mode, bundle, form, fetchExistingBundles]);

    const calculateRemainingDays = (startTime, expireTime) => {
        if (startTime && expireTime) {
            const start = dayjs(startTime);
            const expire = dayjs(expireTime);
            if (start.isValid() && expire.isValid()) {
                const days = expire.diff(start, 'day');
                setRemainingDays(days);
                return days;
            }
        }
        setRemainingDays(0);
        return 0;
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            setLoading(true);
            const bundleData = {
                ...values,
                start_time: timeConverter.localToUtc(values.start_time),
                expire_time: timeConverter.localToUtc(values.expire_time),
                permiss_id: permissionId,
                user_id: userId,
                bundle_id: values.bundle_id
            };

            if (mode === 'assign') {
                await assignBundleToPermission(userId, permissionId, bundleData);
                message.success('Bundle assigned successfully');
            } else {
                await updateBundleAssignment(userId, permissionId, bundleData);
                message.success('Bundle updated successfully');
            }

            form.resetFields();
            onSuccess();
        } catch (error) {
            console.error(`Error ${mode === 'assign' ? 'assigning' : 'updating'} bundle:`, error);
            message.error(`Failed to ${mode === 'assign' ? 'assign' : 'update'} bundle`);
            message.error(error.response?.data?.error || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (date, dateString, fieldName) => {
        const startTime = fieldName === 'start_time' ? date : form.getFieldValue('start_time');
        const expireTime = fieldName === 'expire_time' ? date : form.getFieldValue('expire_time');
        calculateRemainingDays(startTime, expireTime);
    };

    const isAssigned = (bundleId) => {
        return assignedBundles.some(assignedBundle => assignedBundle.id === bundleId);
    };

    const disabledDate = (current) => {
        const today = dayjs();
        const yesterday = today.add(-1, 'day');
        const tenYearsLater = today.add(10, 'year');
        return current && (current < yesterday || current > tenYearsLater);
    };

    return (
        <Modal
            title={mode === 'assign' ? "Assign Bundle" : "Edit Bundle Assignment"}
            visible={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="bundle_id"
                    label="Bundle"
                    rules={[{ required: true, message: 'Please select a bundle!' }]}
                >
                    <Select 
                        disabled={mode === 'edit'} 
                        loading={bundlesLoading}
                        notFoundContent={bundlesLoading ? <Spin size="small" /> : null}
                    >
                        {existingBundles.map(bundle => (
                            <Option key={bundle.id} value={bundle.id} disabled={!bundle.active || (mode === 'assign' && isAssigned(bundle.id))}>
                                {bundle.name} {!bundle.active && '(Inactive)'} {isAssigned(bundle.id) && <Tag color="orange">已分配</Tag>}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="start_time"
                    label="Start Date"
                    rules={[{ required: true, message: 'Please select the start date!' }]}
                >
                    <DatePicker
                        format="YYYY-MM-DD"
                        onChange={(date, dateString) => handleDateChange(date, dateString, 'start_time')}
                        disabledDate={disabledDate}
                        allowClear={false}
                    />
                </Form.Item>
                <Form.Item
                    name="expire_time"
                    label="Expire Date"
                    rules={[{ required: true, message: 'Please select the expire date!' }]}
                >
                    <DatePicker
                        format="YYYY-MM-DD"
                        onChange={(date, dateString) => handleDateChange(date, dateString, 'expire_time')}
                        disabledDate={disabledDate}
                        allowClear={false}
                    />
                </Form.Item>
                <Form.Item label="Total days">
                    <strong>{remainingDays} days</strong>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default React.memo(BundleModal);